import type {
	BaseSuggestionHandlerConfig,
	HandlerStrategyMap,
	SuggestionHandlerFn
} from './types';
import type { Suggestion } from '~/components/SearchSuggest/types';

import { isHistoryItem } from './utils';

/**
 * Base strategy handler following the Strategy Pattern
 *
 * SOLID Principles Applied:
 * - Single Responsibility: Handles strategy selection and delegation
 * - Open/Closed: Easy to add new strategies without modifying this code
 * - Dependency Inversion: Depends on handler abstractions, not concrete implementations
 *
 * @param handlerMap - Map of suggestion types to their handler functions
 * @param fallbackHandler - Optional fallback for unknown types
 * @returns Handler function that delegates to appropriate strategy
 */
export function createStrategyHandler<
	TConfig extends BaseSuggestionHandlerConfig
>(
	handlerMap: HandlerStrategyMap<TConfig>,
	fallbackHandler?: SuggestionHandlerFn<TConfig>
) {
	/**
	 * Main handler that delegates to the appropriate strategy
	 *
	 * @param suggestion - The selected suggestion
	 * @param config - Configuration for handling the selection
	 */
	async function handleSuggestion(
		suggestion: Suggestion,
		config: TConfig
	): Promise<void> {
		// Determine handler key
		// Route pure history items (type='research') to 'research' handler
		// Other types route by searchType
		const handlerKey = isHistoryItem(suggestion)
			? 'research'
			: suggestion.searchType;

		// Get handler from map
		const handler = handlerMap[handlerKey];

		if (!handler) {
			// Use fallback or warn
			if (fallbackHandler) {
				await fallbackHandler(suggestion, config);
			} else {
				console.warn(`No handler found for suggestion type: ${handlerKey}`);
			}
			return;
		}

		// Execute handler
		await handler(suggestion, config);
	}

	return {
		handleSuggestion
	};
}

/**
 * Create a handler map builder with type safety
 * Follows Builder Pattern for fluent API
 */
export function createHandlerMapBuilder<
	TConfig extends BaseSuggestionHandlerConfig
>() {
	const handlers: HandlerStrategyMap<TConfig> = {};

	return {
		/**
		 * Register a handler for a suggestion type
		 */
		register(type: string, handler: SuggestionHandlerFn<TConfig>) {
			handlers[type] = handler;
			return this;
		},

		/**
		 * Register the same handler for multiple types
		 */
		registerMultiple(types: string[], handler: SuggestionHandlerFn<TConfig>) {
			types.forEach(type => {
				handlers[type] = handler;
			});
			return this;
		},

		/**
		 * Build the final handler map
		 */
		build(): HandlerStrategyMap<TConfig> {
			return { ...handlers };
		}
	};
}
